import { z } from 'zod';
import { TileType } from '@/models/TileTypes';
import { Signal } from '@/models/data/Signal';
import {
  ChipInstanceIdSchema,
  CircuitType,
  CircuitTypeSchema,
  GlobalTilePositionSchema,
  InterfaceLinkInstanceIdSchema,
  RotationSchema,
  Side,
  SideSchema,
} from '@/schemas/common';

// Re-export for backward compatibility
export { Side, SideSchema };

export enum TileSideIOType {
  INPUT = 'input',
  OUTPUT = 'output',
  INPUT_OUTPUT = 'input_output',
  NONE = 'none',
}

export const TileTypeSchema: z.ZodType<TileType> = z.union([
  z.nativeEnum(TileType),
  z.string().regex(/^extension:[a-z][a-z0-9.-]{0,79}\/[a-z][a-z0-9-]{0,39}$/),
]) as z.ZodType<TileType>;

export const TileSideIOTypeSchema = z.nativeEnum(TileSideIOType);

export const SideSignalSchema = z.nativeEnum(Signal).nullable();

export const SideSignalsSchema = z.object({
  top: SideSignalSchema,
  right: SideSignalSchema,
  bottom: SideSignalSchema,
  left: SideSignalSchema,
});

export const ConnectionFlagsSchema = z.object({
  [Side.TOP]: z.boolean(),
  [Side.RIGHT]: z.boolean(),
  [Side.BOTTOM]: z.boolean(),
  [Side.LEFT]: z.boolean(),
});

export const BundleInterfaceStateSchema = z.object({
  connectedInterface: z
    .object({
      chipInstanceId: ChipInstanceIdSchema,
      circuitType: CircuitTypeSchema,
      interfaceLinkId: InterfaceLinkInstanceIdSchema,
    })
    .optional(),
  signalId: z.string().min(1).max(256).optional(),
  bit: z.number().int().min(0).max(4095).optional(),
});
export type BundleInterfaceState = z.infer<typeof BundleInterfaceStateSchema>;

export const BundleConfigurationSchema = z
  .object({
    width: z.number().int().positive().max(4096),
    signalIds: z.array(z.string().min(1).max(256)).optional(),
    signalOrigin: z.string().min(1).max(256).optional(),
    signalLabels: z.record(z.string(), z.string().min(1).max(80)).optional(),
    sides: z
      .partialRecord(SideSchema, z.array(z.number().int().nonnegative()))
      .optional(),
  })
  .refine(
    (bundle) =>
      !bundle.signalIds ||
      (bundle.signalIds.length === bundle.width &&
        new Set(bundle.signalIds).size === bundle.width),
    { message: 'Bundle signals must have one distinct persistent ID per lane' },
  )
  .refine(
    (bundle) =>
      Object.values(bundle.sides ?? {}).every(
        (lanes) =>
          lanes.every((lane) => lane < bundle.width) &&
          new Set(lanes).size === lanes.length,
      ),
    { message: 'Bundle lane mappings must contain unique lanes within the bundle width' },
  );

export const TileMetaSchema = z
  .object({
    cable: z.boolean().optional(),
    bundle: BundleConfigurationSchema.optional(),
    tint: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/)
      .optional(),
    inputs: SideSignalsSchema,
    outputs: SideSignalsSchema,
    rotation: RotationSchema,
    connections: ConnectionFlagsSchema.optional(),
    /** When partOfChip has a value, this tile is part of the chips placed structure in a logic circuit
     *  If null, it's a tile that is not tied to a placed chip instance
     */
    partOfChip: ChipInstanceIdSchema.nullable(),
  })
  .refine((meta) => !meta.cable || !meta.bundle, {
    message: 'A count-free cable cannot also have a legacy fixed-lane configuration',
  });

export const TileInstanceDtoSchema = z
  .object({
    id: z.string(),
    tileType: TileTypeSchema,
    globalTileLocation: GlobalTilePositionSchema,
    meta: TileMetaSchema,
    state: z.unknown(),
    logicNodeIds: z.partialRecord(SideSchema, z.string()).optional(),
  })
  .refine(
    (tile) =>
      !tile.meta.cable ||
      (tile.tileType === TileType.Wire &&
        tile.globalTileLocation.circuitType === CircuitType.ChipLogic),
    {
      message: 'A count-free cable must be a Wire tile in the logic circuit',
      path: ['meta', 'cable'],
    },
  );

export type TileInstanceDto = z.infer<typeof TileInstanceDtoSchema>;

export const InterfaceConnectionSchema = z.object({
  chipInstanceId: ChipInstanceIdSchema,
  circuitType: CircuitTypeSchema,
  interfaceLinkId: InterfaceLinkInstanceIdSchema,
});

export type InterfaceConnection = z.infer<typeof InterfaceConnectionSchema>;

export type InterfaceTileState<TState> = {
  connectedInterface: InterfaceConnection;
} & TState;
